/**
 * Small authoring helpers for the six world agents (see docs/art-rollout/AUTHORING.md "v2").
 */
import { t, type L } from '../../text';
import type { ActivityType, Drawing, Scaffold, SkillId, SubjectCategory } from '../model';

/** Default scaffold of a v2 level (docs §A.1.3): L1 trace, L2 guides, L3 main shapes, L4 none. */
export const scaffoldForLevel = (level: number): Scaffold => (level <= 1 ? 'trace' : level === 2 ? 'guides' : level === 3 ? 'mainShapes' : 'none');

export interface StubMeta {
  subject: string;
  category: SubjectCategory;
  skills: SkillId[];
  minutes: number;
  activity: ActivityType;
}

/**
 * Placeholder drawing for a planned v2 id: borrows the steps + art of `borrow` (a drawing of the same
 * world) so the app runs before the art lands. Always artStatus 'pending' + `placeholder: true`.
 */
export function stub(id: string, title: L, meta: StubMeta, borrow: Drawing): Drawing {
  return {
    ...borrow,
    id,
    title,
    subject: meta.subject,
    category: meta.category,
    skills: meta.skills,
    minutes: meta.minutes,
    activity: meta.activity,
    artStatus: 'pending',
    placeholder: true,
    remix: undefined,
    composition: undefined,
    thenNowOf: undefined,
    crossover: undefined,
  };
}

/** Real drawings win over stubs with the same id (add a real drawing → its stub disappears). */
export function withStubs(real: Drawing[], stubs: Drawing[]): Drawing[] {
  const ids = new Set(real.map((d) => d.id));
  return [...real, ...stubs.filter((s) => !ids.has(s.id))];
}

/** Child-friendly labels for common figure part names (self-check lists). Unknown parts fall back to the name. */
export const PART_LABELS: Record<string, L> = {
  head: t('Head', 'Đầu'),
  ears: t('Ears', 'Tai'),
  face: t('Face', 'Mặt'),
  eyes: t('Eyes', 'Mắt'),
  body: t('Body', 'Thân'),
  tail: t('Tail', 'Đuôi'),
  paws: t('Paws', 'Bàn chân'),
  legs: t('Legs', 'Chân'),
  arms: t('Arms', 'Tay'),
  wings: t('Wings', 'Cánh'),
  beak: t('Beak', 'Mỏ'),
  fins: t('Fins', 'Vây'),
  shell: t('Shell', 'Mai'),
  mane: t('Mane', 'Bờm'),
  hat: t('Hat', 'Mũ'),
  hair: t('Hair', 'Tóc'),
  petals: t('Petals', 'Cánh hoa'),
  leaves: t('Leaves', 'Lá'),
  stem: t('Stem', 'Thân cây'),
  trunk: t('Trunk', 'Thân cây'),
  cap: t('Cap', 'Mũ nấm'),
  roof: t('Roof', 'Mái nhà'),
  door: t('Door', 'Cửa'),
  window: t('Window', 'Cửa sổ'),
  handle: t('Handle', 'Quai'),
  wheels: t('Wheels', 'Bánh xe'),
  sail: t('Sail', 'Buồm'),
  rays: t('Rays', 'Tia nắng'),
  pattern: t('Pattern', 'Hoa văn'),
  stripes: t('Stripes', 'Sọc'),
  spots: t('Spots', 'Chấm'),
  whiskers: t('Whiskers', 'Ria'),
  collar: t('Collar', 'Vòng cổ'),
  cape: t('Cape', 'Áo choàng'),
  frosting: t('Frosting', 'Kem'),
  cup: t('Cup', 'Cốc'),
  steam: t('Steam', 'Hơi nóng'),
};

/** 3-5 self-check items from a figure's silhouette parts (plus `face` when present). */
export function checklistFor(d: Drawing): { part: string; label: L }[] {
  const fig = d.art;
  if (!fig) return [t('Main shape', 'Hình chính'), t('Details', 'Chi tiết'), t('Outline', 'Nét viền')].map((label, i) => ({ part: `p${i}`, label }));
  const parts: string[] = [];
  for (const l of fig.layers) if (l.k === 'region' && l.sil && l.p && !parts.includes(l.p)) parts.push(l.p);
  for (const l of fig.layers) if (l.p === 'face' && !parts.includes('face')) parts.push('face');
  for (const l of fig.layers) if (l.k === 'region' && l.p && !parts.includes(l.p)) parts.push(l.p);
  const pick = parts.slice(0, 5);
  while (pick.length < 3) pick.push(pick.length ? `detail${pick.length}` : 'shape');
  return pick.map((p) => ({ part: p, label: PART_LABELS[p] ?? { en: humanize(p), vi: humanize(p) } }));
}

const humanize = (s: string) => {
  const w = s.replace(/[-_:]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return w ? w[0]!.toUpperCase() + w.slice(1) : 'Part';
};
